// src/pages/Predictions2026.js
// 2026 Season Predictions Page - 2026 Scoring

import React, { useState, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import PredictionForm2026 from '../components/PredictionForm2026';
import UserPredictions2026 from '../components/UserPredictions2026';
import { AuthContext } from '../context/AuthContext';

function Predictions2026() {
  const { user, loading } = useContext(AuthContext);
  const [showPointsBreakdown, setShowPointsBreakdown] = useState(false);
  
  if (!loading && !user) {
    return <Navigate to="/login" />;
  }
  
  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">F1 Predictions - 2026 Season</h1>
      <p className="text-gray-400 mb-6">Predict race outcomes and compete on the leaderboard</p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-bold mb-4">Make Your Predictions</h2>
          <ul className="space-y-4 pl-5 mb-6">
            <li>✨ Predict top 5 places (earn points to P10)</li>
            <li>🏁 All predictions close 2 minutes before qualifying</li>
            <li>📊 Refresh the page after submitting to see your predictions</li>
          </ul>
          <PredictionForm2026 />

          {/* Ko-fi widget */}
          <div className="mt-6">
            <iframe
              id="kofiframe"
              src="https://ko-fi.com/f1tipping/?hidefeed=true&widget=true&embed=true&preview=true"
              style={{ border: 'none', width: '100%', padding: '4px', background: '#f9f9f9' }}
              height="712"
              title="f1tipping"
            />
          </div>
        </div>
              
        <div>
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div 
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setShowPointsBreakdown(!showPointsBreakdown)}
            >
              <h2 className="text-xl font-bold text-gray-800">2026 Scoring</h2>
              <span className="text-gray-800">{showPointsBreakdown ? '▲' : '▼'}</span>
            </div>
            
            {showPointsBreakdown && (
              <div className="mt-6 space-y-4">
                <div className="bg-blue-100 p-4 rounded border-l-4 border-blue-500">
                  <p className="text-black font-semibold mb-2">💡 How Scoring Works:</p>
                  <p className="text-black text-sm">
                    You earn the F1 championship points your selected driver actually earns. You also get <strong>double points</strong> if you selected the correct position. All 3 podium correct = <strong>triple points on podium</strong>. All 5 positions correct = <strong>4× the base score (320 points)</strong>!
                  </p>
                </div>

                {/* Position Points Table */}
                <div className="bg-gray-100 p-4 rounded">
                  <h4 className="font-bold text-black mb-3">Position Points (Feature Races)</h4>
                  <div className="bg-white p-4 rounded border border-gray-300">
                    <div className="grid grid-cols-10 gap-2 mb-2">
                      {['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'].map(pos => (
                        <div key={pos} className="text-center text-xs font-semibold text-gray-700">{pos}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-10 gap-2">
                      {[25, 18, 15, 12, 10, 8, 6, 4, 2, 1].map((pts, idx) => (
                        <div key={idx} className="text-center">
                          <div className="text-lg font-bold text-blue-600">{pts}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bonuses */}
                <div className="bg-green-100 p-4 rounded border-l-4 border-green-500">
                  <h4 className="font-bold text-black mb-3">Position Bonuses</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="text-black">
                      <strong>Correct Position (×2):</strong> When your predicted driver finishes in the position you selected, you get <strong>double points</strong>
                      <br/><em className="text-gray-700">Example: Predict P1 and finish P1 = 25 × 2 = 50 points</em>
                    </li>
                    <li className="text-black">
                      <strong>All 3 Podium Correct (P1-P3) (×3):</strong> You get <strong>triple points on podium</strong>
                      <br/><em className="text-gray-700">Example: P1 (50) + P2 (36) + P3 (30) + 58 bonus = 174 points</em>
                    </li>
                    <li className="text-black">
                      <strong>All 5 Correct (P1-P5) (×4):</strong> You get <strong>4× the base score</strong>
                      <br/><em className="text-gray-700">Example: Base 80 points → All correct = 320 points</em>
                    </li>
                  </ul>
                </div>

                {/* Other Predictions */}
                <div className="bg-yellow-100 p-4 rounded border-l-4 border-yellow-500">
                  <h4 className="font-bold text-black mb-2">Other Predictions (Fixed Points)</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="text-black"><strong>Feature Races:</strong> 4 points each</li>
                    <li className="text-black"><strong>Sprint Races:</strong> 1 point each</li>
                    <li className="text-black"><em>Applies to: Pole Position, Fastest Lap, First Retirement, Driver/Team Head-to-Head</em></li>
                  </ul>
                </div>

                {/* Most Places Gained */}
                <div className="bg-green-100 p-4 rounded border-l-4 border-green-500">
                  <h4 className="font-bold text-black mb-2">🚀 Most Places Gained</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="text-black"><strong>Correct Prediction:</strong> 1 point</li>
                    <li className="text-black"><em>Select the driver who gains the most positions from grid to finish</em></li>
                    <li className="text-black"><em>If multiple drivers tie, all users who predicted any tied driver get the point</em></li>
                  </ul>
                </div>

                {/* Sprint Races Note */}
                <div className="bg-orange-100 p-4 rounded border-l-4 border-orange-500">
                  <h4 className="font-bold text-black mb-2">👟 Sprint Races (Reduced Points)</h4>
                  <p className="text-black text-sm">
                    Sprint races use lower point values: P1=8, P2=7, P3=6, P4=5, P5=4, P6=3, P7=2, P8=1
                    <br/>
                    <strong>Bonuses still apply:</strong> ×2 for correct position, ×3 for all 3 podium, ×4 for all 5 correct
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <UserPredictions2026 />
        </div>
      </div>
    </div>
  );
}

export default Predictions2026;